import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle } from "lucide-react";
import { useSecuritySettings } from "@/hooks/useSecuritySettings";
import { normalizeReportReasons } from "@/lib/reportReasons";

interface ReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentType: 'video' | 'comment' | 'ad';
  contentId: string;
}


export const ReportDialog = ({ open, onOpenChange, contentType, contentId }: ReportDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: securitySettings } = useSecuritySettings();
  const reportReasons = normalizeReportReasons(securitySettings?.content_moderation?.report_reasons);
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !reason) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('content_reports')
        .insert({
          reporter_id: user.id,
          content_type: contentType,
          content_id: contentId,
          reason,
          description: description.trim() || null,
          status: 'pending'
        });

      if (error) throw error;

      toast({
        title: "Signalement envoyé",
        description: "Merci, notre équipe va examiner votre signalement."
      });

      onOpenChange(false);
      setReason("");
      setDescription("");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'envoyer le signalement"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            {contentType === "comment" ? "Signaler ce commentaire" : "Signaler ce contenu"}

          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <Label>Raison du signalement *</Label>
            <RadioGroup value={reason} onValueChange={setReason}>
              {reportReasons.map(r => (
                <div key={r.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={r.value} id={r.value} />
                  <Label htmlFor={r.value} className="cursor-pointer font-normal">
                    {r.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optionnelle)</Label>
            <Textarea
              id="description"
              placeholder="Donnez plus de détails sur le problème..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={!reason || loading}>
              {loading ? "Envoi..." : "Envoyer le signalement"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
